/**
 * The pieces of an inbox row, in the anatomy Linear's inbox uses: who it is
 * from (a round avatar with a small badge for the kind of event), what it is
 * about (a title, and a line saying what happened), when, and one status glyph
 * on the right. Measured from the reference: 28px avatar, 14px badge with a
 * surface-coloured ring, 6px unread dot, 14px status glyph.
 *
 * @module components/inbox/InboxRowParts
 */
import {
  CircleDotIcon,
  InfoIcon,
  RadarIcon,
  SparklesIcon,
  UserCheckIcon,
  UserPlusIcon,
  type LucideIcon,
} from "lucide-react";

import { cn } from "../../lib/utils";
import type { InboxItem } from "../../state/inbox";
import { Avatar, AvatarFallback, personInitials } from "../ui/avatar";
import { InspectorAvatar } from "../regulation/inspector/InspectionStatus";
import { notificationSourceLabel } from "./inboxPresentation";

/** The badge on the avatar: what kind of event this is. */
const KIND_BADGE: Record<InboxItem["kind"], { icon: LucideIcon; label: string }> = {
  "policy-review": { icon: RadarIcon, label: "Regulatory update" },
  invitation: { icon: UserPlusIcon, label: "Invitation" },
  "agent-notification": { icon: SparklesIcon, label: "Assistant update" },
  "workflow-approval": { icon: UserCheckIcon, label: "Approval" },
};

function actorInitials(item: InboxItem): string {
  switch (item.kind) {
    case "policy-review":
      return item.regulationTitle.split(/[\s—–-]+/)[0]?.slice(0, 4).toUpperCase() ?? "";
    case "invitation":
      return personInitials({ name: item.organizationName });
    case "agent-notification":
      return item.source === "inspector" ? "IN" : "EV";
    case "workflow-approval":
      return personInitials({ name: item.startedByEmail ?? item.workflowName });
  }
}

/** The 28px actor avatar with the event badge overlapping its corner. */
export function InboxActorAvatar({ item, className }: { item: InboxItem; className?: string }) {
  const badge = KIND_BADGE[item.kind];
  const inspector = item.kind === "agent-notification" && item.source === "inspector";
  return (
    <span className={cn("relative size-7 shrink-0", className)}>
      {inspector ? (
        <InspectorAvatar filled className="size-7 rounded-full" />
      ) : (
        <Avatar className="size-7">
          <AvatarFallback className="bg-foreground/[0.08] text-[9px] font-semibold tracking-tight text-foreground/75">
            {actorInitials(item)}
          </AvatarFallback>
        </Avatar>
      )}
      <span
        role="img"
        aria-label={badge.label}
        // Linear's badge: a small light disc on the avatar's corner, the glyph
        // in ink, a ring of the surface so it reads as sitting on top.
        className="absolute -right-1 -bottom-1 flex size-3.5 items-center justify-center rounded-full bg-background text-foreground ring-2 ring-background"
      >
        <badge.icon aria-hidden className="size-2.5" strokeWidth={2.25} />
      </span>
    </span>
  );
}

/** What happened, in one line under the title — Linear's "Marked as completed by GitHub". */
export function inboxRowSubtitle(item: InboxItem): string {
  switch (item.kind) {
    case "policy-review": {
      const count = item.affectedPolicies.length;
      return count === 0
        ? `Flagged by Monitor · ${item.jurisdiction}`
        : `Flagged by Monitor · ${count} ${count === 1 ? "policy" : "policies"} affected`;
    }
    case "invitation":
      return `Invited you to join as ${item.role}`;
    case "agent-notification":
      return `${notificationSourceLabel(item.source)} · ${item.organizationName}`;
    case "workflow-approval":
      return `Waiting for your approval · ${item.organizationName}`;
  }
}

export type InboxRowStatus =
  | { kind: "settled" }
  | { kind: "snoozed" }
  | { kind: "severity"; severity: "high" | "medium" | "low" }
  | { kind: "invitation" };

export function inboxRowStatus(
  item: InboxItem,
  triage: { settled?: boolean | undefined; snoozedUntil?: string | undefined },
): InboxRowStatus {
  if (triage.settled) return { kind: "settled" };
  if (Date.parse(triage.snoozedUntil ?? "") > Date.now()) return { kind: "snoozed" };
  if (item.kind === "invitation") return { kind: "invitation" };
  return { kind: "severity", severity: item.kind === "workflow-approval" ? "high" : item.severity };
}

/**
 * A solid disc with a mark cut into it, for the states that are filled. A
 * lucide icon cannot do this: its ring and its mark share one stroke colour,
 * so filling the disc left a pale ring around it in dark mode.
 */
function FilledDisc({ mark, className }: { mark: string; className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={cn("size-3.5", className)}>
      <circle cx="12" cy="12" r="11" fill="currentColor" />
      <path
        d={mark}
        fill="none"
        stroke="var(--disc-mark)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const STATUS_GLYPH: Record<
  string,
  { icon: LucideIcon; label: string; className: string } | { disc: string; label: string; className: string }
> = {
  settled: {
    disc: "m7.5 12.5 3 3 6-7",
    label: "Done",
    className: "text-primary [--disc-mark:var(--primary-foreground)]",
  },
  snoozed: { icon: CircleDotIcon, label: "Snoozed", className: "text-muted-foreground" },
  high: {
    disc: "M12 7v5.5M12 16.5v.01",
    label: "Action needed",
    className: "text-destructive [--disc-mark:white]",
  },
  medium: { icon: CircleDotIcon, label: "Review", className: "text-warning-foreground" },
  low: { icon: InfoIcon, label: "Informational", className: "text-info-foreground" },
  invitation: { icon: UserPlusIcon, label: "Invitation", className: "text-muted-foreground" },
};

/** The one glyph on the row's right: the state of the thing, at a glance. */
export function InboxStatusGlyph({ status, className }: { status: InboxRowStatus; className?: string }) {
  const key = status.kind === "severity" ? status.severity : status.kind;
  const glyph = STATUS_GLYPH[key]!;
  return (
    <span
      role="img"
      aria-label={glyph.label}
      title={glyph.label}
      data-inbox-priority=""
      className={cn("flex size-4 shrink-0 items-center justify-center", className)}
    >
      {"disc" in glyph ? (
        <FilledDisc mark={glyph.disc} className={glyph.className} />
      ) : (
        <glyph.icon aria-hidden className={cn("size-3.5", glyph.className)} strokeWidth={2} />
      )}
    </span>
  );
}
